import React, { useState } from "react";

import PaymentService from "../../../services/payment/PaymentService";
import MerchantsService from "../../../services/merchants/MerchantsService";
import TerminalService from "../../../services/terminals/TerminalService";
import StorageService from "../../../services/storage/StorageService";
import SystemMessageService from "../../../services/systemMessage/SystemMessageService";
import VersionService from "../../../services/Version/VersionService";

import OnePayConfigAdapter from "../../../utils/payment/OnePayConfigAdapter";
import OnePayConfigValidator from "../../../utils/payment/OnePayConfigValidator";

import { PageSpinner } from "@cdk-uip/react-page-spinner";
import Header from "../header/Header";
import OnePay from "@cdkglobal/onepay-component/cjs/components/_onePay/OnePay";
import ErrorDialog from "../error/common/ErrorDialog";
import SystemMessage from "../message/SystemMessage";

import useOnMount from "../hook/UseOnMount";

import "./PaymentWrapper.css";
import DealerNameService from "../../../services/dealerNames/DealerNameService";

const StorageConstants = {
  MERCHANT: "MERCHANT",
  TERMINAL: "TERMINAL",
  DEALER_NAME: "DEALER_NAME"
};

const PaymentWrapper = () => {
  const [isLoading, setIsLoading] = useState(true);

  const [onePayData, setOnePayData] = useState(undefined);

  const [message, setMessage] = useState({});

  const [error, setError] = useState({});

  /**
   * Loads the system messages
   */
  useOnMount(async () => {
    try {
      const systemMessage = await SystemMessageService.getMessage();

      if (systemMessage && systemMessage.title && systemMessage.message)
        setMessage({
          ...systemMessage,
          isOpen: true,
          onOk: doNotShowAgain => {
            setMessage({ isOpen: false });

            if (doNotShowAgain)
              SystemMessageService.doNotShowMessageAgain(systemMessage.id);
          }
        });
    } catch (e) {
      console.error(
        "An error has occurred while trying to load One Pay system message",
        e
      );
    }
  });

  /**
   * Validates subscription information
   */
  useOnMount(async () => {
    try {
      await PaymentService.validateSubscription();
    } catch (e) {
      console.error(
        "An error has occurred while validating the subscriptions",
        e
      );
      showError(
        "Please verify if the Fortellis Subscription is correctly configured!"
      );
    }
  });

  /**
   * Loads the payment data
   */
  useOnMount(async () => {
    try {
      let paymentInfo = await PaymentService.getPaymentData();
      if (
        !paymentInfo.accFlag &&
        !paymentInfo.accountingI2PFlag &&
        paymentInfo.balanceDue <= 0
      ) {
        const documentId = paymentInfo.documentId
          .toString()
          .toUpperCase()
          .startsWith("CM")
          ? paymentInfo.origDocId
          : paymentInfo.documentId;
        const validateInvoiceTransaction = await PaymentService.validateInvoiceTransaction(
          documentId
        );
        paymentInfo = {
          validateInvoiceTransaction: validateInvoiceTransaction.length,
          ...paymentInfo
        };
      }
      const {
        serviceViewActive
      } = await PaymentService.getServiceViewMapping();

      if (paymentInfo.isAccountingSecondLoad) {
        await sendAccountingSecondLoadPayment();
      } else {
        // Load payment
        let paymentData = OnePayConfigAdapter.adapt(paymentInfo).toJson();

        // close code added for SNXT
        if (paymentInfo.callBackUrl) {
          paymentData.close.onClose = () => {
            window.parent.location = paymentInfo.callBackUrl;
          };
        }

        const validationResult = OnePayConfigValidator.validate(paymentInfo);

        if (!validationResult.isValid) {
          console.error(
            "The Payment Data does not match the business rules ",
            validationResult.message
          );
          showError(validationResult.message);
        } else {
          setOnePayData({
            ...paymentData,
            allowEditEmail: !serviceViewActive,
            allowEditPhone: !serviceViewActive,
            loadMerchants: getMerchants,
            loadTerminals: getTerminals,
            loadVersion: getVersion,
            loadDealerStoresNames: getDealerStoresNames
          });
        }
      }

      setIsLoading(false);
    } catch (e) {
      console.error("An error has occurred while trying to load One Pay", e);
      showError("An error has occurred while trying to load the application.");
    }
  });

  const sendAccountingSecondLoadPayment = async () => {
    try {
      const result = await PaymentService.sendAccountingPayment({
        mid: StorageService.getItem(StorageConstants.MERCHANT),
        terminal: StorageService.getItem(StorageConstants.TERMINAL),
        dealerStoresName: StorageService.getItem(StorageConstants.DEALER_NAME)
      });

      if (result.isError) {
        showError(result.message, () => {
          window.close();
        });
      } else {
        window.location = result.paymentURL;
      }
    } catch (e) {
      console.error(
        "An error has occurred while trying to send the accounting second load payment"
      );
      showError("An error happened while trying to send the payment.");
    }
  };

  const showError = errorMessage => {
    setError({
      title: "An Error has Occurred",
      message: errorMessage,
      isOpen: true,
      onOk: () => {
        window.close();
      }
    });
  };

  const getMerchants = async () => {
    const merchants = await MerchantsService.getMerchants();

    return merchants.map(m => {
      return {
        ...m,
        selected: StorageService.getItem(StorageConstants.MERCHANT) === m.mid
      };
    });
  };

  const getTerminals = async () => {
    const terminals = await TerminalService.getTerminals();

    return terminals.map(t => {
      return {
        ...t,
        selected: StorageService.getItem(StorageConstants.TERMINAL) === t.url
      };
    });
  };

  const getVersion = async () => {
    const version = await VersionService.getVersion();

    return version;
  };

  const getDealerStoresNames = async () => {
    const dealerNames = await DealerNameService.getDealerStoresNames();

    return dealerNames.map(d => {
      return {
        ...d,
        selected:
            StorageService.getItem(StorageConstants.DEALER_NAME) === d.name
      };
    });
  };

  return (
    <div>
      <PageSpinner show={isLoading} />
      <Header />
      <ErrorDialog {...error} />
      <SystemMessage {...message} />
      {onePayData && (
        <div className={"onepay-container"}>
          <OnePay {...onePayData}></OnePay>
        </div>
      )}
    </div>
  );
};

export default PaymentWrapper;
